import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Bot, Download, RefreshCw, Lightbulb, TrendingUp, AlertCircle, Calendar, Filter, Database, BarChart3, ExternalLink } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useFilters } from '../hooks/useFilters';
import { usePagination } from '../hooks/usePagination';
import { formatDateTime } from '../lib/utils';
import DataDebugView from '../components/DataDebugView';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

// Map agent names to their dashboard paths
const AGENT_DASHBOARD_MAP = {
  'ReasonExecutiveBrain': '/dashboard/executive-brain',
  'ReasonStockSense': '/dashboard/stock-sense',
  'ReasonSalesIQ': '/dashboard/sales-iq',
  'ReasonCustomer360': '/dashboard/customer-360',
  'ReasonProductMix': '/dashboard/product-mix',
  'ReasonVendorLink': '/dashboard/vendor-link',
  'ReasonStorePulse': '/dashboard/store-pulse'
};

const AgentResults = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [selectedAgent, setSelectedAgent] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showDebugView, setShowDebugView] = useState(false);
  
  // Use custom hooks for filtering and pagination
  const { filters, filteredItems, updateFilter, resetFilters, hasActiveFilters } = useFilters(results, {
    searchFields: ['agentName', 'summary'],
    dateField: 'executedAt'
  });
  
  const { 
    currentPage, 
    totalPages, 
    currentItems: paginatedResults, 
    nextPage, 
    prevPage, 
    goToPage, 
    resetPage,
    hasNextPage,
    hasPrevPage
  } = usePagination(filteredItems, 5);

  // Agents sorted by priority (0 = highest priority)
  const agents = [
    { value: 'ReasonExecutiveBrain', label: 'ReasonExecutiveBrain', category: 'Executive', priority: 0 },
    { value: 'ReasonSalesIQ', label: 'ReasonSalesIQ', category: 'Revenue', priority: 1 },
    { value: 'ReasonStockSense', label: 'ReasonStockSense', category: 'Inventory', priority: 2 },
    { value: 'ReasonStorePulse', label: 'ReasonStorePulse', category: 'Operations', priority: 3 },
    { value: 'ReasonCustomer360', label: 'ReasonCustomer360', category: 'Customer', priority: 4 },
    { value: 'ReasonProductMix', label: 'ReasonProductMix', category: 'Merchandise', priority: 5 },
    { value: 'ReasonPromoIQ', label: 'ReasonPromoIQ', category: 'Sales', priority: 6 },
    { value: 'ReasonReturnIQ', label: 'ReasonReturnIQ', category: 'Operations', priority: 7 },
    { value: 'ReasonVendorLink', label: 'ReasonVendorLink', category: 'Operations', priority: 8 },
    { value: 'ReasonDemandFlow', label: 'ReasonDemandFlow', category: 'Demand', priority: 9 },
  ];

  const fetchResults = async () => {
    if (!selectedAgent) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const params = {
        agent_name: selectedAgent,
        limit: 100
      };
      
      // Add date filters if set
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      
      const response = await axios.get(
        `${BACKEND_URL}/api/agents/results/${user.tenantId}`,
        {
          params,
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      
      setResults(response.data);
      resetPage(); // Reset to first page on new fetch using hook
    } catch (err) {
      setError('Failed to fetch results');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleApplyFilters = () => {
    fetchResults();
  };

  const handleClearFilters = () => {
    setSelectedAgent('');
    setStartDate('');
    setEndDate('');
    resetFilters();
    resetPage();
    setResults([]);
  };

  // Filtering and pagination are now handled by custom hooks

  const downloadCSV = () => {
    if (results.length === 0) return;

    const headers = ['Agent Name', 'Summary', 'Key Insights', 'Recommended Actions', 'Executed At'];
    const rows = results.map(r => {
      // Support both new contract format (keyInsights/recommendedActions) and legacy format (insights/recommendations)
      const insights = r.keyInsights?.length > 0 
        ? r.keyInsights.map(i => i.evidence || i.title || '').join('; ')
        : (r.insights || []).join('; ');
      const actions = r.recommendedActions?.length > 0
        ? r.recommendedActions.map(a => a.action || '').join('; ')
        : (r.recommendations || []).join('; ');
      
      return [
        r.agentName,
        (r.summary || '').replace(/,/g, ';'),
        insights.replace(/,/g, ';'),
        actions.replace(/,/g, ';'),
        r.executedAt
      ];
    });

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `agent_results_${selectedAgent}_${new Date().toISOString()}.csv`;
    link.click();
  };

  useEffect(() => {
    // Removed auto-fetch - user should click "Apply Filter" button
  }, []);

  return (
    <div className="max-w-7xl mx-auto space-y-6" data-testid="agent-results-page">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <Bot className="h-8 w-8 text-blue-600" />
          <div>
            <h1 className="text-3xl font-bold text-slate-900 dark:text-white">AI Agents Results</h1>
            <p className="text-slate-600 dark:text-slate-400">View historical agent execution results and insights</p>
          </div>
        </div>
        
        {/* Debug View Toggle - Only for admin/superadmin */}
        {(user?.role === 'admin' || user?.role === 'superadmin') && (
          <Button
            onClick={() => setShowDebugView(!showDebugView)}
            variant={showDebugView ? "default" : "outline"}
            className="flex items-center gap-2"
            data-testid="toggle-debug-view-btn"
          >
            <Database className="h-4 w-4" />
            {showDebugView ? 'Hide' : 'Show'} Debug View
          </Button>
        )}
      </div>

      {/* Data Debug View - Shows what data is sent to LLM */}
      {showDebugView && user?.tenantId && (
        <DataDebugView tenantId={user.tenantId} />
      )}

      {/* Error Message */}
      {error && (
        <Card className="border-red-200 bg-red-50 dark:bg-red-900/20">
          <CardContent className="pt-6">
            <div className="flex items-start space-x-3">
              <AlertCircle className="h-5 w-5 text-red-600 mt-0.5" />
              <div>
                <h3 className="font-semibold text-red-900 dark:text-red-100">Error</h3>
                <p className="text-sm text-red-800 dark:text-red-200 mt-1">{error}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Control Panel */}
      <Card>
        <CardHeader>
          <CardTitle>Agent Selection & Filters</CardTitle>
          <CardDescription>Choose an agent and apply filters to view historical results</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Agent Selection and Actions */}
          <div className="flex items-center space-x-4">
            <select
              value={selectedAgent}
              onChange={(e) => setSelectedAgent(e.target.value)}
              className="flex-1 px-4 py-3 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-slate-800 dark:text-white"
            >
              <option value="">-- Select Agent --</option>
              {agents.map((agent) => (
                <option key={agent.value} value={agent.value}>
                  {agent.label} ({agent.category})
                </option>
              ))}
            </select>

            <Button
              onClick={fetchResults}
              variant="outline"
              disabled={loading || !selectedAgent}
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>

            <Button
              onClick={downloadCSV}
              variant="outline"
              disabled={filteredItems.length === 0}
            >
              <Download className="h-4 w-4 mr-2" />
              Download CSV
            </Button>
          </div>

          {/* Date Filters */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 pt-4 border-t border-slate-200 dark:border-slate-700">
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                <Calendar className="inline h-4 w-4 mr-1" />
                Start Date
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-slate-800 dark:text-white"
                data-testid="filter-start-date"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                <Calendar className="inline h-4 w-4 mr-1" />
                End Date
              </label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-slate-800 dark:text-white"
                data-testid="filter-end-date"
              />
            </div>
            <div className="flex items-end gap-2">
              <Button
                onClick={handleApplyFilters}
                variant="default"
                className="flex-1"
                disabled={!selectedAgent || loading}
                data-testid="apply-filter-btn"
              >
                <Filter className="h-4 w-4 mr-2" />
                Apply Filter
              </Button>
              <Button
                onClick={handleClearFilters}
                variant="outline"
                className="flex-1"
                disabled={!selectedAgent && !startDate && !endDate}
                data-testid="clear-filter-btn"
              >
                Clear Filters
              </Button>
            </div>
          </div>

          {/* Results Count */}
          {selectedAgent && (
            <div className="text-sm text-slate-600 dark:text-slate-400">
              Showing {paginatedResults.length} of {filteredItems.length} results
              {filteredItems.length !== results.length && ` (filtered from ${results.length} total)`}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Results Table */}
      <Card>
        <CardHeader>
          <CardTitle>Agent Results</CardTitle>
          <CardDescription>Latest analysis results from selected agent</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Bot className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>
                {results.length === 0 
                  ? 'No results yet. Select an agent and click Run Agent to see results.'
                  : 'No results found for the selected date range.'}
              </p>
            </div>
          ) : (
            <>
              <div className="space-y-4">
                {paginatedResults.map((result, index) => (
                <div key={index} className="border border-slate-200 dark:border-slate-700 rounded-lg p-6 hover:shadow-md transition-shadow">
                  {/* Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <Bot className="h-6 w-6 text-blue-600" />
                      <div>
                        <h3 className="text-lg font-semibold text-slate-900 dark:text-white">{result.agentName}</h3>
                        <p className="text-xs text-slate-500">
                          {result.executedAt ? formatDateTime(result.executedAt) : 'Not available'}
                        </p>
                      </div>
                    </div>
                    {/* View Dashboard Button */}
                    {AGENT_DASHBOARD_MAP[result.agentName] && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="flex items-center gap-2 text-blue-600 border-blue-200 hover:bg-blue-50"
                        onClick={() => navigate(AGENT_DASHBOARD_MAP[result.agentName])}
                        data-testid={`view-dashboard-${result.agentName}`}
                      >
                        <BarChart3 className="h-4 w-4" />
                        View Dashboard
                        <ExternalLink className="h-3 w-3" />
                      </Button>
                    )}
                  </div>

                  {/* Summary */}
                  <div className="mb-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                    <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-100 mb-2">Summary</h4>
                    <p className="text-sm text-blue-800 dark:text-blue-200">{result.summary}</p>
                  </div>

                  {/* Key Insights - supports both new contract format and legacy format */}
                  {((result.keyInsights && result.keyInsights.length > 0) || (result.insights && result.insights.length > 0)) && (
                    <div className="mb-4">
                      <h4 className="text-sm font-semibold text-slate-900 dark:text-white mb-3 flex items-center">
                        <TrendingUp className="h-4 w-4 mr-2 text-purple-600" />
                        Key Insights ({(result.keyInsights || result.insights || []).length})
                      </h4>
                      <div className="space-y-2">
                        {(result.keyInsights && result.keyInsights.length > 0 
                          ? result.keyInsights 
                          : (result.insights || []).map(i => ({ evidence: i, title: i }))
                        ).map((insight, idx) => {
                          // Handle new contract format: { title, metric, evidence, scope, timeWindow }
                          const title = insight.title || `Insight ${idx + 1}`;
                          const evidence = insight.evidence || insight.text || (typeof insight === 'string' ? insight : JSON.stringify(insight));
                          const metric = insight.metric;
                          const scope = insight.scope;
                          const timeWindow = insight.timeWindow;
                          
                          return (
                            <div key={idx} className="flex items-start space-x-3 p-3 border-l-4 rounded border-purple-400 bg-purple-50 dark:bg-purple-900/20">
                              <span className="inline-flex items-center justify-center w-6 h-6 text-xs font-bold text-purple-700 dark:text-purple-300 bg-white dark:bg-slate-700 rounded-full border-2 border-purple-300 flex-shrink-0">
                                {idx + 1}
                              </span>
                              <div className="flex-1">
                                {title !== evidence && (
                                  <p className="text-sm font-medium text-slate-900 dark:text-white mb-1">{title}</p>
                                )}
                                <p className="text-sm text-slate-700 dark:text-slate-300">{evidence}</p>
                                <div className="flex flex-wrap gap-2 mt-2">
                                  {metric && (
                                    <span className="text-xs font-medium px-2 py-1 bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200 rounded">
                                      Metric: {metric}
                                    </span>
                                  )}
                                  {scope && (
                                    <span className="text-xs font-medium px-2 py-1 bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200 rounded">
                                      Scope: {scope}
                                    </span>
                                  )}
                                  {timeWindow && (
                                    <span className="text-xs font-medium px-2 py-1 bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200 rounded">
                                      Period: {timeWindow}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Recommended Actions - supports both new contract format and legacy format */}
                  {((result.recommendedActions && result.recommendedActions.length > 0) || (result.recommendations && result.recommendations.length > 0)) ? (
                    <div>
                      <h4 className="text-sm font-semibold text-slate-900 dark:text-white mb-3 flex items-center">
                        <Lightbulb className="h-4 w-4 mr-2 text-yellow-600" />
                        Recommended Actions ({(result.recommendedActions || result.recommendations || []).length})
                      </h4>
                      
                      <div className="space-y-2">
                        {(result.recommendedActions && result.recommendedActions.length > 0 
                          ? result.recommendedActions 
                          : (result.recommendations || []).map(r => ({ action: typeof r === 'string' ? r : r.text || JSON.stringify(r), priority: 'MEDIUM' }))
                        ).map((rec, idx) => {
                          // Handle new contract format: { action, ownerRole, priority, expectedImpact }
                          const actionText = rec.action || rec.text || (typeof rec === 'string' ? rec : JSON.stringify(rec));
                          const ownerRole = rec.ownerRole || 'Operations';
                          const priority = (rec.priority || 'MEDIUM').toUpperCase();
                          const expectedImpact = rec.expectedImpact || '';
                          
                          // Priority color coding
                          const priorityColors = {
                            HIGH: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200 border-red-300',
                            MEDIUM: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-200 border-yellow-300',
                            LOW: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200 border-green-300'
                          };
                          const priorityClass = priorityColors[priority] || priorityColors.MEDIUM;
                          
                          return (
                            <div key={idx} className="flex items-start space-x-3 p-3 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg hover:shadow-md transition-shadow">
                              <span className="inline-flex items-center justify-center w-7 h-7 text-sm font-bold text-white bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-full flex-shrink-0">
                                {idx + 1}
                              </span>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-slate-900 dark:text-white">{actionText}</p>
                                <div className="flex items-center gap-2 mt-2 flex-wrap">
                                  <span className={`text-xs font-medium px-2 py-1 rounded border ${priorityClass}`}>
                                    {priority}
                                  </span>
                                  {ownerRole && (
                                    <span className="text-xs px-2 py-1 bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200 rounded">
                                      Owner: {ownerRole}
                                    </span>
                                  )}
                                  {expectedImpact && (
                                    <span className="text-xs px-2 py-1 bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200 rounded">
                                      Impact: {expectedImpact}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ) : (
                    <div className="p-4 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-center">
                      <Lightbulb className="h-8 w-8 mx-auto mb-2 text-slate-400" />
                      <p className="text-sm text-slate-500 dark:text-slate-400">
                        {result.dataDiagnostics?.notes || 'No recommendations generated for this run'}
                      </p>
                      <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">
                        {result.dataDiagnostics?.missingCollections?.length > 0 && (
                          <>Missing data: {result.dataDiagnostics.missingCollections.join(', ')}</>
                        )}
                        {!result.dataDiagnostics?.missingCollections?.length && result.agentName === 'ReasonVendorLink' && 'Vendor analysis requires vendor master data'}
                        {!result.dataDiagnostics?.missingCollections?.length && result.agentName === 'ReasonDemandFlow' && 'Forecasting requires historical sales data'}
                        {!result.dataDiagnostics?.missingCollections?.length && !['ReasonVendorLink', 'ReasonDemandFlow'].includes(result.agentName) && 'This may indicate insufficient data for analysis'}
                      </p>
                    </div>
                  )}

                  {/* PromoIQ Granular Findings Table - Only for ReasonPromoIQ */}
                  {result.agentName === 'ReasonPromoIQ' && result.topFindings && result.topFindings.length > 0 && (
                    <div className="mt-4">
                      <h4 className="text-sm font-semibold text-slate-900 dark:text-white mb-3 flex items-center">
                        <AlertCircle className="h-4 w-4 mr-2 text-orange-600" />
                        Top-10 Granular Findings
                      </h4>
                      <div className="overflow-x-auto">
                        <table className="min-w-full text-xs">
                          <thead className="bg-slate-100 dark:bg-slate-700">
                            <tr>
                              <th className="px-2 py-2 text-left font-medium text-slate-600 dark:text-slate-300">#</th>
                              <th className="px-2 py-2 text-left font-medium text-slate-600 dark:text-slate-300">SKU</th>
                              <th className="px-2 py-2 text-left font-medium text-slate-600 dark:text-slate-300">Store</th>
                              <th className="px-2 py-2 text-left font-medium text-slate-600 dark:text-slate-300">Promo</th>
                              <th className="px-2 py-2 text-right font-medium text-slate-600 dark:text-slate-300">Discount %</th>
                              <th className="px-2 py-2 text-right font-medium text-slate-600 dark:text-slate-300">Lift %</th>
                              <th className="px-2 py-2 text-right font-medium text-slate-600 dark:text-slate-300">Promo Qty</th>
                              <th className="px-2 py-2 text-right font-medium text-slate-600 dark:text-slate-300">Base Qty</th>
                              <th className="px-2 py-2 text-left font-medium text-slate-600 dark:text-slate-300">Flag</th>
                              <th className="px-2 py-2 text-left font-medium text-slate-600 dark:text-slate-300">Action</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-200 dark:divide-slate-600">
                            {result.topFindings.slice(0, 10).map((finding, idx) => (
                              <tr key={idx} className="hover:bg-slate-50 dark:hover:bg-slate-800">
                                <td className="px-2 py-2 text-slate-700 dark:text-slate-300">{finding.rank || idx + 1}</td>
                                <td className="px-2 py-2 font-medium text-slate-900 dark:text-white">{finding.sku}</td>
                                <td className="px-2 py-2 text-slate-700 dark:text-slate-300">{finding.store}</td>
                                <td className="px-2 py-2 text-slate-700 dark:text-slate-300">{finding.promo_code}</td>
                                <td className="px-2 py-2 text-right text-slate-700 dark:text-slate-300">{finding.discount_pct?.toFixed(1)}%</td>
                                <td className={`px-2 py-2 text-right font-medium ${
                                  finding.lift_pct > 0 ? 'text-green-600' : finding.lift_pct < 0 ? 'text-red-600' : 'text-slate-600'
                                }`}>
                                  {finding.lift_pct > 0 ? '+' : ''}{finding.lift_pct?.toFixed(1)}%
                                </td>
                                <td className="px-2 py-2 text-right text-slate-700 dark:text-slate-300">{finding.promo_qty}</td>
                                <td className="px-2 py-2 text-right text-slate-700 dark:text-slate-300">{finding.base_qty}</td>
                                <td className="px-2 py-2">
                                  <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                                    finding.issue_flag === 'STOP_PROMO' ? 'bg-red-100 text-red-800' :
                                    finding.issue_flag === 'CAP_DISCOUNT' ? 'bg-yellow-100 text-yellow-800' :
                                    finding.issue_flag === 'SCALE_PROMO' ? 'bg-green-100 text-green-800' :
                                    finding.issue_flag === 'STORE_ANOMALY' ? 'bg-orange-100 text-orange-800' :
                                    'bg-slate-100 text-slate-800'
                                  }`}>
                                    {finding.issue_flag}
                                  </span>
                                </td>
                                <td className="px-2 py-2 text-slate-600 dark:text-slate-400 max-w-xs truncate" title={finding.suggested_action}>
                                  {finding.suggested_action}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Historical Observations - Only for ReasonPromoIQ */}
                  {result.agentName === 'ReasonPromoIQ' && result.historicalInsights?.observations && result.historicalInsights.observations.length > 0 && (
                    <div className="mt-4 p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
                      <h4 className="text-sm font-semibold text-amber-900 dark:text-amber-100 mb-2 flex items-center">
                        <Calendar className="h-4 w-4 mr-2" />
                        Historical Observations ({result.historicalInsights.runs_analyzed} previous runs analyzed)
                      </h4>
                      <ul className="text-sm text-amber-800 dark:text-amber-200 space-y-1">
                        {result.historicalInsights.observations.map((obs, idx) => (
                          <li key={idx}>{obs}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-6 pt-4 border-t border-slate-200 dark:border-slate-700">
                <div className="text-sm text-slate-600 dark:text-slate-400">
                  Page {currentPage} of {totalPages}
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={prevPage}
                    disabled={!hasPrevPage}
                    variant="outline"
                    size="sm"
                  >
                    Previous
                  </Button>
                  {[...Array(Math.min(5, totalPages))].map((_, i) => {
                    const pageNum = i + 1;
                    return (
                      <Button
                        key={i}
                        onClick={() => goToPage(pageNum)}
                        variant={currentPage === pageNum ? 'default' : 'outline'}
                        size="sm"
                        className={currentPage === pageNum ? 'bg-blue-600 text-white' : ''}
                      >
                        {pageNum}
                      </Button>
                    );
                  })}
                  <Button
                    onClick={nextPage}
                    disabled={!hasNextPage}
                    variant="outline"
                    size="sm"
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AgentResults;